import Head from 'next/head';
import { useTranslations } from 'next-intl';
import { OrganizationSchema, WebSiteSchema } from '@/components/json-ld';
import config from '@/config';
import { defaultShareImage, SHARE_IMAGE } from '@/utils/share-image';

// TODO: Pull this from the config
const SITE_ROOT = config.rootUrl;

// Calculate static metadata values at module level (outside component render)
// This ensures they're only calculated once when the module loads, not during render
const LAST_BUILT = Date.now();
const UPDATED_TIME = new Date().toISOString();

const DefaultHead = () => {
  const t = useTranslations('defaultHead');

  const defaults = {
    // The committed card: pages with one of their own override it through <SEO imageUrl>.
    shareImage: defaultShareImage(),
    title: `Bidali | ${t('title')}`,
    description: t('description'),
    author: 'Bidali HQ',
    lastBuilt: LAST_BUILT,
    google: {
      name: 'Bidali',
      about: t('title'),
      description: t('description'),
      applicationCategory: 'Software',
      author: 'Bidali HQ',
    },
    facebook: {
      title: t('title'),
      description: t('description'),
      type: 'website',
      siteName: 'Bidali',
      updatedTime: UPDATED_TIME,
      admins: '517607527',
      page: '1748858885180222',
    },
    twitter: {
      handle: '@bidalihq',
      title: t('title'),
      description: t('description'),
      creator: '@bidalihq',
    },
  };

  return (
    <>
      <Head>
        {/* No maximum-scale or user-scalable: visitors must be able to pinch-zoom. */}
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <title key="title">{defaults.title}</title>
        <meta name="description" content={defaults.description} key="description" />
        <meta name="author" content={defaults.author} key="author" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={defaults.twitter.handle} />
        <meta name="twitter:title" content={defaults.twitter.title} key="twittertitle" />
        <meta
          name="twitter:description"
          content={defaults.twitter.description}
          key="twitterdescription"
        />
        <meta name="twitter:creator" content={defaults.twitter.creator} />
        <meta name="twitter:image" content={defaults.shareImage} key="twitterimage" />
        <meta name="twitter:image:src" content={defaults.shareImage} key="twitterimagesrc" />
        <meta name="twitter:image:alt" content={defaults.twitter.title} key="twitterimagealt" />

        <meta property="og:title" content={defaults.facebook.title} key="ogtitle" />
        <meta
          property="og:description"
          content={defaults.facebook.description}
          key="ogdescription"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_ROOT} key="ogurl" />
        <meta property="og:image" content={defaults.shareImage} key="ogimage" />
        {/* Every share image, the default and the ones drawn per page, is this size and type. */}
        <meta property="og:image:width" content={String(SHARE_IMAGE.width)} />
        <meta property="og:image:height" content={String(SHARE_IMAGE.height)} />
        <meta property="og:image:type" content={SHARE_IMAGE.type} />
        <meta property="og:image:alt" content={defaults.facebook.title} key="ogimagealt" />
        <meta property="og:site_name" content={defaults.facebook.siteName} />
        <meta property="og:updated_time" content={defaults.facebook.updatedTime} />
        <meta property="og:rich_attachment" content="true" />
        <meta property="fb:admins" content={defaults.facebook.admins} />
        <meta property="fb:pages" content={defaults.facebook.page} />

        <meta itemProp="name" content={defaults.google.name} />
        <meta itemProp="about" content={defaults.google.about} key="itempropabout" />
        <meta
          itemProp="description"
          content={defaults.google.description}
          key="itempropdescription"
        />
        <meta itemProp="author" content={defaults.google.author} />
        <meta itemProp="image" content={defaults.shareImage} key="itempropimage" />
        <meta itemProp="applicationCategory" content={defaults.google.applicationCategory} />

        {/* <link rel="preload" as="image" href="https://assets.bidali.com/company/bidali/logo.avif" /> */}
      </Head>
      {/* Siblings of <Head>, not children: each renders its own <Head>, and next/head drops nested ones. */}
      <OrganizationSchema />
      <WebSiteSchema />
    </>
  );
};

export default DefaultHead;
